import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import T2TTopNav from "../Layout/T2TTopNav";
import T2TWbsProgress from "./T2TWbsProgress";
import { getData, postData, putData, deleteData } from "../../../FetchApiServices";
import { useToast, useConfirm, usePermissions } from "../Layout/T2TUIProvider";

// BUG-71: dashboard widget palette
const STATUS_COLORS  = { in_progress: "#2563EB", approved: "#16A34A", rejected: "#DC2626", on_hold: "#D97706", completed: "#7C3AED" };
const PRIORITY_COLORS = { critical: "#DC2626", high: "#D97706", medium: "#2563EB", low: "#16A34A" };
const SLA_COLORS     = { on_track: "#16A34A", at_risk: "#D97706", breached: "#DC2626", no_sla: "#94A3B8" };

function greetingFor(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

const statusColor = (s) => ({ active: "#36B82A", draft: "#D97706", inactive: "#94A3B8" }[s] || "#94A3B8");

const EMPTY_WF = { name: "", description: "", group_id: "", company_id: "", division_id: "", department_id: "" };

// R9 — Starter templates (UI-only, user can customise before creating)
const TEMPLATES = [
  { key: "vendor_onboarding", name: "Vendor Onboarding",   description: "Onboard a new vendor with finance, legal, and compliance checks." },
  { key: "capex_approval",    name: "Capex Approval",      description: "Capital expenditure request through finance and management approval." },
  { key: "employee_exit",     name: "Employee Exit",       description: "Employee offboarding process from HR review to asset return." },
  { key: "it_asset_request",  name: "IT Asset Request",    description: "Request for new IT assets such as laptops, phones, or software." },
  { key: "travel_approval",   name: "Travel Approval",     description: "Domestic or international travel request with manager approval." },
  { key: "leave_approval",    name: "Leave Approval",      description: "Employee leave request routed to manager and HR." },
  { key: "purchase_order",    name: "Purchase Order",      description: "Procurement request with department head and finance signoff." },
];

const T2TDashboard = () => {
  const navigate = useNavigate();
  const toast              = useToast();
  const confirm            = useConfirm();
  const { hasPermission }  = usePermissions();

  const [workflows, setWorkflows]             = useState([]);
  const [wfLoading, setWfLoading]             = useState(false);
  const [wfError, setWfError]                 = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWf, setNewWf]                     = useState(EMPTY_WF);
  const [creating, setCreating]               = useState(false);
  const [createError, setCreateError]         = useState("");
  const [orgUnits, setOrgUnits]               = useState([]);
  const [editModal,  setEditModal]             = useState(false);
  const [editWf,     setEditWf]               = useState(EMPTY_WF);
  const [editingId,  setEditingId]            = useState(null);
  const [editing,    setEditing]              = useState(false);
  const [editError,  setEditError]            = useState("");
  const [showTemplates, setShowTemplates]     = useState(false);
  const [wfTab, setWfTab]                     = useState("active"); // "active" | "archived"
  // BUG-70: dashboard Workflows card search + status sub-filter
  const [wfSearch,     setWfSearch]     = useState("");
  const [wfStatusSub,  setWfStatusSub]  = useState("all"); // "all" | "active" | "draft" — only used when wfTab === "active"
  const [showWbs, setShowWbs]                 = useState(false); // WBS progress — hidden by default

  // API-10 — dashboard stats
  const [stats, setStats] = useState({ active_requests: null, completed_requests: null, my_queue_count: null });

  // BUG-71 — extended counts for status / priority / SLA / today / my-pending widgets
  const [counts, setCounts] = useState(null);
  const currentUser = useSelector((state) => state?.user?.users?.[0]);

  useEffect(() => { fetchWorkflows(); fetchOrgUnits(); fetchStats(); fetchCounts(); }, []);

  // BUG-71: dashboard rich-counts payload from /t2t/requests/counts
  const fetchCounts = async () => {
    const resp = await getData("t2t/requests/counts");
    if (!resp.error) {
      const d = resp.data?.data || resp.data || {};
      setCounts(d);
    }
  };

  const fetchWorkflows = async () => {
    setWfLoading(true);
    setWfError("");
    const resp = await getData("t2t/workflows?page=1&limit=50");
    if (resp.error) {
      setWfError(resp.error.message || "Failed to load workflows.");
    } else {
      const raw = resp.data?.data;
      const list =
        Array.isArray(raw)            ? raw           :
        Array.isArray(raw?.data)      ? raw.data      :
        Array.isArray(raw?.workflows) ? raw.workflows :
        Array.isArray(raw?.rows)      ? raw.rows      :
        Array.isArray(resp.data)      ? resp.data     :
        [];
      setWorkflows(list);
    }
    setWfLoading(false);
  };

  const fetchOrgUnits = async () => {
    const resp = await getData("t2t/org-units");
    if (!resp.error) {
      const raw = resp.data?.data;
      setOrgUnits(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    }
  };

  // API-10
  const fetchStats = async () => {
    const resp = await getData("t2t/dashboard/stats");
    if (!resp.error) {
      const d = resp.data?.data || resp.data || {};
      setStats({
        active_requests:    d.active_requests    ?? null,
        completed_requests: d.completed_requests ?? null,
        my_queue_count:     d.my_queue_count     ?? null,
      });
    }
  };

  const openCreateModal = () => {
    setNewWf(EMPTY_WF);
    setCreateError("");
    setShowTemplates(false);
    setShowCreateModal(true);
  };

  // R11 — Clone: open Create modal pre-filled with "Copy of …"
  const handleClone = async (e, wf) => {
    e.stopPropagation();
    // Use API-4 if available, otherwise pre-fill Create modal as fallback
    const resp = await postData(`t2t/workflows/${wf.workflow_id}/clone`, {});
    if (resp.error) {
      // Fallback: open create modal pre-filled
      setNewWf({
        name:          `Copy of ${wf.name}`,
        description:   wf.description   || "",
        group_id:      wf.group_id      ?? wf.t2t_group_id      ?? "",
        company_id:    wf.company_id    ?? wf.t2t_company_id    ?? "",
        division_id:   wf.division_id   ?? wf.t2t_division_id   ?? "",
        department_id: wf.department_id ?? wf.t2t_department_id ?? "",
      });
      setCreateError("");
      setShowTemplates(false);
      setShowCreateModal(true);
    } else {
      const clonedId = resp.data?.data?.workflow_id;
      toast(`"${wf.name}" cloned successfully.`, "success");
      if (clonedId) {
        navigate(`/t2t-builder/${clonedId}`);
      } else {
        fetchWorkflows();
      }
    }
  };

  const handleCreateWorkflow = async () => {
    if (!newWf.name.trim())    { setCreateError("Workflow name is required."); return; }
    if (!newWf.group_id)       { setCreateError("Group is required."); return; }
    if (!newWf.company_id)     { setCreateError("Company is required."); return; }
    setCreateError("");
    setCreating(true);

    const payload = {
      name:        newWf.name,
      description: newWf.description,
      group_id:    Number(newWf.group_id),
      company_id:  Number(newWf.company_id),
      ...(newWf.division_id   && { division_id:   Number(newWf.division_id) }),
      ...(newWf.department_id && { department_id: Number(newWf.department_id) }),
    };

    const resp = await postData("t2t/workflows", payload);
    if (resp.error) {
      setCreateError(resp.error.message || "Failed to create workflow.");
      setCreating(false);
      return;
    }
    const wfId = resp.data?.data?.workflow_id;
    setCreating(false);
    setShowCreateModal(false);
    setNewWf(EMPTY_WF);
    navigate(`/t2t-builder/${wfId}`);
  };

  const openEditModal = (e, wf) => {
    e.stopPropagation();
    setEditError("");
    setEditingId(wf.workflow_id);
    setEditWf({
      name:          wf.name          || "",
      description:   wf.description   || "",
      group_id:      wf.group_id      ?? wf.t2t_group_id      ?? "",
      company_id:    wf.company_id    ?? wf.t2t_company_id    ?? "",
      division_id:   wf.division_id   ?? wf.t2t_division_id   ?? "",
      department_id: wf.department_id ?? wf.t2t_department_id ?? "",
    });
    setEditModal(true);
  };

  const handleEditWorkflow = async () => {
    if (!editWf.name.trim()) { setEditError("Workflow name is required."); return; }
    if (!editWf.group_id)    { setEditError("Group is required."); return; }
    if (!editWf.company_id)  { setEditError("Company is required."); return; }
    setEditError("");
    setEditing(true);
    const payload = {
      name:        editWf.name,
      description: editWf.description,
      group_id:    Number(editWf.group_id),
      company_id:  Number(editWf.company_id),
      ...(editWf.division_id   && { division_id:   Number(editWf.division_id) }),
      ...(editWf.department_id && { department_id: Number(editWf.department_id) }),
    };
    const resp = await putData(`t2t/workflows/${editingId}`, payload);
    if (resp.error) {
      setEditError(resp.error.message || "Failed to update workflow.");
    } else {
      toast("Workflow updated.", "success");
      setEditModal(false);
      fetchWorkflows();
    }
    setEditing(false);
  };

  const handleDeleteWorkflow = async (e, wfId) => {
    e.stopPropagation();
    const ok = await confirm("All stages and transitions will also be deleted. This cannot be undone.", "Delete Workflow?");
    if (!ok) return;
    const resp = await deleteData(`t2t/workflows/${wfId}`);
    if (resp.error) { toast(resp.error.message || "Failed to delete workflow.", "error"); return; }
    toast("Workflow deleted.", "success");
    fetchWorkflows();
    fetchStats();
  };

  // Phase 21 — Archive / Unarchive
  const handleArchive = async (e, wfId, wfName) => {
    e.stopPropagation();
    const ok = await confirm(
      "Archived workflows cannot receive new requests. Active requests will be put on hold.",
      `Archive "${wfName}"?`
    );
    if (!ok) return;
    const resp = await postData(`t2t/workflows/${wfId}/archive`, {});
    if (resp.error) { toast(resp.error.message || "Failed to archive.", "error"); return; }
    toast("Workflow archived.", "success");
    fetchWorkflows();
  };

  const handleUnarchive = async (e, wfId) => {
    e.stopPropagation();
    const resp = await postData(`t2t/workflows/${wfId}/unarchive`, {});
    if (resp.error) { toast(resp.error.message || "Failed to unarchive.", "error"); return; }
    toast("Workflow restored to draft.", "success");
    fetchWorkflows();
  };

  // Stats derived from live data
  const totalWf   = workflows.filter((w) => w.workflow_status !== "archived").length;
  const activeWf  = workflows.filter((w) => w.workflow_status === "active").length;
  const draftWf   = workflows.filter((w) => w.workflow_status === "draft").length;
  const archivedWf = workflows.filter((w) => w.workflow_status === "archived").length;

  // BUG-70: derived list — tab + search + status sub-filter combine cleanly
  const wfSearchLc = wfSearch.trim().toLowerCase();
  const filteredWorkflows = workflows.filter((w) => {
    const inTab = wfTab === "archived" ? w.workflow_status === "archived" : w.workflow_status !== "archived";
    if (!inTab) return false;
    if (wfTab === "active" && wfStatusSub !== "all" && w.workflow_status !== wfStatusSub) return false;
    if (wfSearchLc) {
      const hay = `${w.name || ""} ${w.description || ""}`.toLowerCase();
      if (!hay.includes(wfSearchLc)) return false;
    }
    return true;
  });

  // Cascading org unit filters
  const groups      = orgUnits.filter((o) => o.type === "Group");
  const companies   = orgUnits.filter((o) => o.type === "Company"    && String(o.parent_id) === String(newWf.group_id));
  const divisions   = orgUnits.filter((o) => o.type === "Division"   && String(o.parent_id) === String(newWf.company_id));
  const departments = orgUnits.filter((o) => o.type === "Department" && String(o.parent_id) === String(newWf.division_id));

  // Cascading dropdowns for edit modal
  const editCompanies   = orgUnits.filter((o) => o.type === "Company"    && String(o.parent_id) === String(editWf.group_id));
  const editDivisions   = orgUnits.filter((o) => o.type === "Division"   && String(o.parent_id) === String(editWf.company_id));
  const editDepartments = orgUnits.filter((o) => o.type === "Department" && String(o.parent_id) === String(editWf.division_id));

  return (
    <>
      <T2TTopNav
        title="Dashboard"
        actions={
          hasPermission("workflow_builder.create") ? (
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={openCreateModal}>
              + New Workflow
            </button>
          ) : null
        }
      />

      <div className="t2t-content">
        <div className="t2t-page-title">Dashboard</div>
        <p className="t2t-page-sub">Overview of your workflows and recent activity.</p>

        {/* BUG-71: greeting + SLA health + today's actions strip */}
        {(() => {
          const pendingCount = Number(counts?.my_queue) || 0;
          const sla = counts?.sla_breakdown || { on_track: 0, at_risk: 0, breached: 0, no_sla: 0 };
          const slaTotal = (sla.on_track + sla.at_risk + sla.breached + sla.no_sla) || 1;
          const today = counts?.today_actions || { submits: 0, approvals: 0, rejections: 0 };
          const totalActions = today.submits + today.approvals + today.rejections;
          const pct = (n) => Math.round((n / slaTotal) * 100);

          return (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12 }}>
              {/* Greeting */}
              <div className="t2t-stat-card" style={{ minHeight: 110 }}>
                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {greetingFor(new Date().getHours())},
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
                  {currentUser?.name || "T2T User"}
                </div>
                <div style={{ fontSize: 12, marginTop: 6, color: "var(--t2t-text-muted)" }}>
                  You have <strong style={{ color: pendingCount > 0 ? "#DC2626" : "#16A34A", fontSize: 14 }}>{pendingCount}</strong> pending action{pendingCount !== 1 ? "s" : ""}.
                </div>
                {pendingCount > 0 && (
                  <button
                    type="button"
                    className="t2t-btn t2t-btn-sm t2t-btn-primary"
                    style={{ marginTop: 8, fontSize: 11 }}
                    onClick={() => navigate("/t2t-queue")}
                  >Go to Queue →</button>
                )}
              </div>

              {/* SLA Health */}
              <div className="t2t-stat-card" style={{ minHeight: 110 }}>
                <div className="t2t-stat-label">SLA Health (in-flight)</div>
                <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginTop: 12, background: "#F1F5F9" }}>
                  {sla.on_track > 0 && <div style={{ flexBasis: `${pct(sla.on_track)}%`, background: SLA_COLORS.on_track }} title={`${sla.on_track} on track`} />}
                  {sla.at_risk  > 0 && <div style={{ flexBasis: `${pct(sla.at_risk)}%`,  background: SLA_COLORS.at_risk  }} title={`${sla.at_risk} at risk`} />}
                  {sla.breached > 0 && <div style={{ flexBasis: `${pct(sla.breached)}%`, background: SLA_COLORS.breached }} title={`${sla.breached} breached`} />}
                  {sla.no_sla   > 0 && <div style={{ flexBasis: `${pct(sla.no_sla)}%`,   background: SLA_COLORS.no_sla   }} title={`${sla.no_sla} no SLA`} />}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8, fontSize: 10 }}>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: SLA_COLORS.on_track, marginRight: 4 }} />On track {sla.on_track}</span>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: SLA_COLORS.at_risk, marginRight: 4 }} />At risk {sla.at_risk}</span>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: SLA_COLORS.breached, marginRight: 4 }} />Breached {sla.breached}</span>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: SLA_COLORS.no_sla, marginRight: 4 }} />No SLA {sla.no_sla}</span>
                </div>
              </div>

              {/* Today's Actions (24h) */}
              <div className="t2t-stat-card" style={{ minHeight: 110 }}>
                <div className="t2t-stat-label">Today&apos;s Actions (last 24h)</div>
                <div style={{ fontSize: 28, fontWeight: 700, marginTop: 2 }}>{totalActions}</div>
                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                  <span><i className="bi bi-arrow-up-right" style={{ color: "#2563EB", marginRight: 3 }} />{today.submits} submitted</span>
                  <span><i className="bi bi-check2-circle" style={{ color: "#16A34A", marginRight: 3 }} />{today.approvals} approved</span>
                  <span><i className="bi bi-x-circle" style={{ color: "#DC2626", marginRight: 3 }} />{today.rejections} rejected</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* BUG-71: status donut + priority bar + my-pending top 5 */}
        {(() => {
          const sb = counts?.status_breakdown || { in_progress: 0, approved: 0, rejected: 0, on_hold: 0, completed: 0 };
          const statusData = Object.entries(sb)
            .map(([key, value]) => ({ key, name: key.replace(/_/g, " "), value }))
            .filter((d) => d.value > 0);
          const statusTotal = statusData.reduce((s, d) => s + d.value, 0);

          const pb = counts?.priority_breakdown || { critical: 0, high: 0, medium: 0, low: 0 };
          const prioTotal = (pb.critical + pb.high + pb.medium + pb.low) || 1;
          const prioPct = (n) => (n / prioTotal) * 100;

          const myPending = Array.isArray(counts?.my_pending_top) ? counts.my_pending_top : [];

          return (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              {/* Status donut */}
              <div className="t2t-stat-card" style={{ minHeight: 220 }}>
                <div className="t2t-stat-label">Request Status</div>
                {statusTotal === 0 ? (
                  <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "var(--t2t-text-muted)" }}>No requests yet</div>
                ) : (
                  <>
                    <div style={{ height: 150 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={2}>
                            {statusData.map((entry, i) => (
                              <Cell key={i} fill={STATUS_COLORS[entry.key] || "#94A3B8"} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 10, marginTop: 4 }}>
                      {statusData.map((d) => (
                        <span key={d.key}>
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: STATUS_COLORS[d.key] || "#94A3B8", marginRight: 4 }} />
                          {d.name} ({d.value})
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Priority distribution bar */}
              <div className="t2t-stat-card" style={{ minHeight: 220 }}>
                <div className="t2t-stat-label">Priority Distribution (active)</div>
                {prioTotal <= 1 && pb.critical + pb.high + pb.medium + pb.low === 0 ? (
                  <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "var(--t2t-text-muted)" }}>No active requests</div>
                ) : (
                  <>
                    <div style={{ display: "flex", height: 18, borderRadius: 4, overflow: "hidden", marginTop: 16, background: "#F1F5F9" }}>
                      {pb.critical > 0 && <div style={{ flexBasis: `${prioPct(pb.critical)}%`, background: PRIORITY_COLORS.critical }} title={`Critical: ${pb.critical}`} />}
                      {pb.high     > 0 && <div style={{ flexBasis: `${prioPct(pb.high)}%`,     background: PRIORITY_COLORS.high     }} title={`High: ${pb.high}`} />}
                      {pb.medium   > 0 && <div style={{ flexBasis: `${prioPct(pb.medium)}%`,   background: PRIORITY_COLORS.medium   }} title={`Medium: ${pb.medium}`} />}
                      {pb.low      > 0 && <div style={{ flexBasis: `${prioPct(pb.low)}%`,      background: PRIORITY_COLORS.low      }} title={`Low: ${pb.low}`} />}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14, fontSize: 11 }}>
                      {[
                        { k: "critical", label: "Critical" },
                        { k: "high",     label: "High"     },
                        { k: "medium",   label: "Medium"   },
                        { k: "low",      label: "Low"      },
                      ].map(({ k, label }) => (
                        <div key={k} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: PRIORITY_COLORS[k] }} />
                          <span style={{ flex: 1, color: "var(--t2t-text-muted)" }}>{label}</span>
                          <span style={{ fontWeight: 600 }}>{pb[k]}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* My pending actions top 5 */}
              <div className="t2t-stat-card" style={{ minHeight: 220 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span className="t2t-stat-label">My Pending Actions</span>
                  {myPending.length > 0 && (
                    <button type="button" className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 10 }} onClick={() => navigate("/t2t-queue")}>View All →</button>
                  )}
                </div>
                {myPending.length === 0 ? (
                  <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "var(--t2t-text-muted)" }}>
                    🎉 Inbox zero — nothing waiting for you.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                    {myPending.map((r) => {
                      const due = r.due_at ? new Date(r.due_at) : null;
                      const isBreached = due && due < new Date();
                      const dueLabel = due
                        ? due.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) + ", " + due.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                        : "No SLA";
                      return (
                        <div
                          key={r.request_id}
                          onClick={() => navigate(`/t2t-request/${r.ref_id}`)}
                          style={{ padding: "6px 8px", border: "1px solid var(--t2t-border)", borderRadius: 6, cursor: "pointer", display: "flex", flexDirection: "column", gap: 2, fontSize: 11 }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#F8FAFC"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontWeight: 700, color: "var(--t2t-primary)", fontFamily: "monospace", fontSize: 10 }}>{r.ref_id}</span>
                            {r.priority && (
                              <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: (PRIORITY_COLORS[r.priority] || "#94A3B8") + "22", color: PRIORITY_COLORS[r.priority] || "#94A3B8", textTransform: "uppercase" }}>
                                {r.priority}
                              </span>
                            )}
                          </div>
                          <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
                          <div style={{ display: "flex", justifyContent: "space-between", color: "var(--t2t-text-muted)", fontSize: 10 }}>
                            <span>{r.stage_name}</span>
                            <span style={{ color: isBreached ? "#DC2626" : "var(--t2t-text-muted)" }}>{dueLabel}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* WBS Progress — hidden by default, toggle to show */}
        <div style={{ marginBottom: showWbs ? 24 : 8 }}>
          <button
            type="button"
            onClick={() => setShowWbs((p) => !p)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--t2t-primary)", padding: 0, display: "flex", alignItems: "center", gap: 4 }}
          >
            {showWbs ? "\u25BC" : "\u25B6"} Build Progress (WBS)
          </button>
          {showWbs && (
            <div style={{ marginTop: 10 }}>
              <T2TWbsProgress />
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="t2t-stats-grid">
          <div className="t2t-stat-card">
            <div className="t2t-stat-label">Total Workflows</div>
            <div className="t2t-stat-value" style={{ color: "#36B82A" }}>{totalWf}</div>
            <div className="t2t-stat-meta">{activeWf} active</div>
          </div>
          <div className="t2t-stat-card">
            <div className="t2t-stat-label">Draft Workflows</div>
            <div className="t2t-stat-value" style={{ color: "#D97706" }}>{draftWf}</div>
            <div className="t2t-stat-meta">Not yet published</div>
          </div>
          <div className="t2t-stat-card">
            <div className="t2t-stat-label">Active Requests</div>
            <div className="t2t-stat-value" style={{ color: "#DC2626" }}>
              {stats.active_requests !== null ? stats.active_requests : "—"}
            </div>
            <div className="t2t-stat-meta">
              {stats.my_queue_count !== null ? `${stats.my_queue_count} in your queue` : "This month"}
            </div>
          </div>
          <div className="t2t-stat-card">
            <div className="t2t-stat-label">Completed</div>
            <div className="t2t-stat-value" style={{ color: "#7C3AED" }}>
              {stats.completed_requests !== null ? stats.completed_requests : "—"}
            </div>
            <div className="t2t-stat-meta">All time</div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="t2t-content-grid">

          {/* Workflows */}
          <div className="t2t-card">
            <div className="t2t-card-head">
              <span className="t2t-card-title">📋 Workflows</span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button className={`t2t-tab${wfTab === "active" ? " active" : ""}`} style={{ fontSize: 11, padding: "2px 10px" }} onClick={() => setWfTab("active")}>
                  Active ({totalWf})
                </button>
                <button className={`t2t-tab${wfTab === "archived" ? " active" : ""}`} style={{ fontSize: 11, padding: "2px 10px" }} onClick={() => setWfTab("archived")}>
                  Archived ({archivedWf})
                </button>
                {hasPermission("workflow_builder.create") && (
                  <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={openCreateModal}>+ New</button>
                )}
              </div>
            </div>

            {/* BUG-70: search + status sub-filter (Active tab only) */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0 10px", flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
                <input
                  type="text"
                  className="t2t-fc"
                  style={{ paddingLeft: 28, paddingRight: wfSearch ? 26 : 10, fontSize: 12, height: 30 }}
                  placeholder="Search workflows by name or description…"
                  value={wfSearch}
                  onChange={(e) => setWfSearch(e.target.value)}
                />
                <i className="bi bi-search" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94A3B8", pointerEvents: "none" }} />
                {wfSearch && (
                  <button
                    type="button"
                    onClick={() => setWfSearch("")}
                    aria-label="Clear search"
                    title="Clear search"
                    style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#94A3B8", fontSize: 14, cursor: "pointer", lineHeight: 1, padding: 0 }}
                  >×</button>
                )}
              </div>
              {wfTab === "active" && (
                <div style={{ display: "flex", gap: 4 }}>
                  {[
                    { val: "all",    label: `All (${totalWf})`    },
                    { val: "active", label: `Active (${activeWf})` },
                    { val: "draft",  label: `Draft (${draftWf})`  },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      type="button"
                      className={`t2t-tab${wfStatusSub === opt.val ? " active" : ""}`}
                      style={{ fontSize: 11, padding: "2px 10px" }}
                      onClick={() => setWfStatusSub(opt.val)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {wfLoading && <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading...</div>}
            {wfError   && <div style={{ padding: 12, color: "var(--t2t-danger)", fontSize: 12, background: "#FEF2F2", borderRadius: 6, margin: "0 0 8px" }}>{wfError}</div>}

            {!wfLoading && filteredWorkflows.length === 0 && !wfError && (
              <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>
                {wfSearch || (wfTab === "active" && wfStatusSub !== "all")
                  ? "No workflows match the current filters."
                  : wfTab === "archived" ? "No archived workflows." : "No workflows yet. Click + New to create one."}
              </div>
            )}

            {filteredWorkflows.map((wf) => (
              <div className="t2t-list-row" key={wf.workflow_id} onClick={() => wf.workflow_status !== "archived" ? navigate(`/t2t-builder/${wf.workflow_id}`) : null} style={{ opacity: wf.workflow_status === "archived" ? 0.6 : 1, cursor: wf.workflow_status === "archived" ? "default" : "pointer" }}>
                <div className="t2t-list-icon" style={{ background: wf.workflow_status === "active" ? "#F0FDF4" : wf.workflow_status === "archived" ? "#F1F5F9" : "#F1F5F9" }}>
                  {wf.workflow_status === "active" ? "✅" : wf.workflow_status === "archived" ? "📦" : "📝"}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="t2t-list-title">{wf.name}</div>
                  <div className="t2t-list-meta">
                    {wf.stage_count || 0} stages
                    {wf.description && ` • ${wf.description}`}
                  </div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: statusColor(wf.workflow_status) + "22", color: statusColor(wf.workflow_status), marginRight: 6 }}>
                  {wf.workflow_status || "draft"}
                </span>
                {wf.workflow_status === "active" && (
                  <button
                    className="t2t-btn t2t-btn-success t2t-btn-sm"
                    style={{ padding: "2px 8px", fontSize: 10 }}
                    onClick={(e) => { e.stopPropagation(); navigate(`/t2t-submit/${wf.workflow_id}`); }}
                    title="Submit a new request"
                  >+ Submit</button>
                )}
                {hasPermission("workflow_builder.edit") && (
                  <button
                    className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                    style={{ padding: "2px 6px" }}
                    onClick={(e) => openEditModal(e, wf)}
                    title="Edit workflow"
                  >✏️</button>
                )}
                {hasPermission("workflow_builder.clone") && (
                  <button
                    className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                    style={{ padding: "2px 6px" }}
                    onClick={(e) => handleClone(e, wf)}
                    title="Clone workflow"
                  >⧉</button>
                )}
                {hasPermission("workflow_builder.archive") && wf.workflow_status !== "archived" && (
                  <button
                    className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                    style={{ padding: "2px 6px", fontSize: 12 }}
                    onClick={(e) => handleArchive(e, wf.workflow_id, wf.name)}
                    title="Archive workflow"
                  >📦</button>
                )}
                {wf.workflow_status === "archived" && (
                  <button
                    className="t2t-btn t2t-btn-outline t2t-btn-sm"
                    style={{ padding: "2px 8px", fontSize: 10 }}
                    onClick={(e) => handleUnarchive(e, wf.workflow_id)}
                    title="Restore to draft"
                  >↩ Restore</button>
                )}
                {hasPermission("workflow_builder.clone") && wf.workflow_status === "archived" && (
                  <button
                    className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                    style={{ padding: "2px 6px" }}
                    onClick={(e) => handleClone(e, wf)}
                    title="Clone archived workflow"
                  >⧉</button>
                )}
                {hasPermission("workflow_builder.delete") && wf.workflow_status !== "archived" && (
                  <button
                    className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                    style={{ color: "var(--t2t-danger)", padding: "2px 6px" }}
                    onClick={(e) => handleDeleteWorkflow(e, wf.workflow_id)}
                    title="Delete workflow"
                  >✕</button>
                )}
              </div>
            ))}
          </div>

          {/* Recent Requests — live from API-5 */}
          <RecentRequests />

        </div>
      </div>

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <div className="t2t-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">Create New Workflow</div>
                <div className="t2t-modal-sub">Set up your approval process</div>
              </div>
              <button className="t2t-close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="t2t-modal-body">
              {createError && (
                <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{createError}</div>
              )}

              {/* R9 — Starter Templates */}
              <div style={{ marginBottom: 14 }}>
                <button
                  type="button"
                  className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                  style={{ fontSize: 11, width: "100%", justifyContent: "space-between", display: "flex" }}
                  onClick={() => setShowTemplates((v) => !v)}
                >
                  <span>⚡ Start from a template</span>
                  <span>{showTemplates ? "▲" : "▼"}</span>
                </button>
                {showTemplates && (
                  <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {TEMPLATES.map((tpl) => (
                      <button
                        key={tpl.key}
                        type="button"
                        className="t2t-btn t2t-btn-outline t2t-btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={async () => {
                          if (!newWf.group_id || !newWf.company_id) {
                            setCreateError("Select Group and Company first, then pick a template.");
                            setShowTemplates(false);
                            return;
                          }
                          setShowTemplates(false);
                          setCreating(true);
                          setCreateError("");
                          const resp = await postData("t2t/workflows/from-template", {
                            template_key: tpl.key,
                            group_id:     Number(newWf.group_id),
                            company_id:   Number(newWf.company_id),
                            ...(newWf.division_id   && { division_id:   Number(newWf.division_id) }),
                            ...(newWf.department_id && { department_id: Number(newWf.department_id) }),
                          });
                          if (resp.error) {
                            setCreateError(resp.error.message || "Failed to create from template.");
                            setCreating(false);
                            return;
                          }
                          const wfId = resp.data?.data?.workflow_id;
                          setCreating(false);
                          setShowCreateModal(false);
                          setNewWf(EMPTY_WF);
                          toast(`"${tpl.name}" created with stages and fields. Assign roles and publish!`, "success");
                          navigate(`/t2t-builder/${wfId}`);
                        }}
                      >
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="t2t-fg">
                <label>Workflow Name *</label>
                <input className="t2t-fc" placeholder="e.g. Vendor Onboarding, Capex Request..." value={newWf.name} onChange={(e) => setNewWf({ ...newWf, name: e.target.value })} />
              </div>

              <div className="t2t-fg">
                <label>Description</label>
                <textarea className="t2t-fc" rows="2" placeholder="What process does this workflow manage?" value={newWf.description} onChange={(e) => setNewWf({ ...newWf, description: e.target.value })} />
              </div>

              <div className="t2t-fg">
                <label>Group *</label>
                <select className="t2t-fc" value={newWf.group_id}
                  onChange={(e) => setNewWf({ ...newWf, group_id: e.target.value, company_id: "", division_id: "", department_id: "" })}>
                  <option value="">Select Group...</option>
                  {groups.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Company *</label>
                <select className="t2t-fc" value={newWf.company_id} disabled={!newWf.group_id}
                  onChange={(e) => setNewWf({ ...newWf, company_id: e.target.value, division_id: "", department_id: "" })}>
                  <option value="">Select Company...</option>
                  {companies.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Division</label>
                <select className="t2t-fc" value={newWf.division_id} disabled={!newWf.company_id}
                  onChange={(e) => setNewWf({ ...newWf, division_id: e.target.value, department_id: "" })}>
                  <option value="">Select Division...</option>
                  {divisions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Department</label>
                <select className="t2t-fc" value={newWf.department_id} disabled={!newWf.division_id}
                  onChange={(e) => setNewWf({ ...newWf, department_id: e.target.value })}>
                  <option value="">Select Department...</option>
                  {departments.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="t2t-btn t2t-btn-primary" onClick={handleCreateWorkflow} disabled={creating}>
                {creating ? "Creating..." : "Create Workflow →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Workflow Modal */}
      {editModal && (
        <div className="t2t-overlay" onClick={() => setEditModal(false)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">Edit Workflow</div>
                <div className="t2t-modal-sub">Update name, description or org scope</div>
              </div>
              <button className="t2t-close-btn" onClick={() => setEditModal(false)}>×</button>
            </div>
            <div className="t2t-modal-body">
              {editError && (
                <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{editError}</div>
              )}

              <div className="t2t-fg">
                <label>Workflow Name *</label>
                <input className="t2t-fc" value={editWf.name} onChange={(e) => setEditWf({ ...editWf, name: e.target.value })} placeholder="e.g. Vendor Onboarding" />
              </div>

              <div className="t2t-fg">
                <label>Description</label>
                <textarea className="t2t-fc" rows="2" value={editWf.description} onChange={(e) => setEditWf({ ...editWf, description: e.target.value })} placeholder="What process does this workflow manage?" />
              </div>

              <div className="t2t-fg">
                <label>Group *</label>
                <select className="t2t-fc" value={editWf.group_id}
                  onChange={(e) => setEditWf({ ...editWf, group_id: e.target.value, company_id: "", division_id: "", department_id: "" })}>
                  <option value="">Select Group...</option>
                  {groups.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Company *</label>
                <select className="t2t-fc" value={editWf.company_id} disabled={!editWf.group_id}
                  onChange={(e) => setEditWf({ ...editWf, company_id: e.target.value, division_id: "", department_id: "" })}>
                  <option value="">Select Company...</option>
                  {editCompanies.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Division</label>
                <select className="t2t-fc" value={editWf.division_id} disabled={!editWf.company_id}
                  onChange={(e) => setEditWf({ ...editWf, division_id: e.target.value, department_id: "" })}>
                  <option value="">Select Division...</option>
                  {editDivisions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>

              <div className="t2t-fg">
                <label>Department</label>
                <select className="t2t-fc" value={editWf.department_id} disabled={!editWf.division_id}
                  onChange={(e) => setEditWf({ ...editWf, department_id: e.target.value })}>
                  <option value="">Select Department...</option>
                  {editDepartments.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setEditModal(false)}>Cancel</button>
              <button className="t2t-btn t2t-btn-primary" onClick={handleEditWorkflow} disabled={editing}>
                {editing ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// API-5 — Recent Requests panel (last 5)
const RecentRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const resp = await getData("t2t/requests?page=1&limit=5");
      if (!resp.error) {
        const raw = resp.data?.data;
        const list =
          Array.isArray(raw?.rows)  ? raw.rows  :
          Array.isArray(raw)        ? raw        :
          Array.isArray(resp.data)  ? resp.data  :
          [];
        setRequests(list);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  return (
    <div className="t2t-card">
      <div className="t2t-card-head">
        <span className="t2t-card-title">🔄 Recent Requests</span>
        <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => navigate("/t2t-requests")}>View All</button>
      </div>
      {loading && (
        <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading...</div>
      )}
      {!loading && requests.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>No requests yet.</div>
      )}
      {requests.map((req) => (
        <div className="t2t-list-row" key={req.request_id} onClick={() => navigate(`/t2t-request/${req.request_id}`)}>
          <div className="t2t-list-icon" style={{ background: "#F1F5F9" }}>📄</div>
          <div style={{ flex: 1 }}>
            <div className="t2t-list-title">{req.title}</div>
            <div className="t2t-list-meta">{req.workflow_name} • {req.current_stage}</div>
          </div>
          <span className={`t2t-badge t2t-badge-${req.status}`}>{(req.status || "").replace("_", " ")}</span>
        </div>
      ))}
    </div>
  );
};

export default T2TDashboard;
